import { queryOptions } from "@tanstack/react-query";
import { ApiError } from "@altek/ui/lib/problem";
import { sessionApi } from "./session.api";
import type { Session } from "./session.api";

/**
 * The session query.
 *
 * `staleTime: Infinity` and no automatic refetch: the permission set changes only when
 * something explicitly changes it — a sign-in, a sign-out, a role grant — and every one of
 * those invalidates this key by hand. Refetching it on window focus would re-run the whole
 * shell's permission filter every time the user came back to the tab, for data that had not
 * moved.
 */
export const sessionKeys = {
  all: ["session"] as const,
  current: () => [...sessionKeys.all, "current"] as const,
};

/**
 * `Session | null` — `null` is the SIGNED-OUT value, written by the auth provider on sign-out.
 *
 * It exists so signing out does not have to remove this query from the cache. Removing it
 * strands the provider's mounted observer on its last result: React Query drops the entry but
 * the observer keeps reporting the old session, so the app still believes somebody is signed
 * in until the page is reloaded. A cached `null` is a state the observer can actually see.
 */
export function sessionQuery() {
  return queryOptions<Session | null>({
    queryKey: sessionKeys.current(),
    queryFn: () => sessionApi.describe(),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    /**
     * A 401 is the answer, not a failure.
     *
     * Retrying it would delay the sign-in screen by two backoff intervals and put two more
     * 401s in the server's logs for a question already answered. Anything else — a dropped
     * connection, a 502 from a restarting API — is worth one retry, because "not signed in"
     * and "could not ask" must not look the same to the user.
     */
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 1;
    },
  });
}
